/*
 * Copyright (c) 2018-2023 MemberCenter, a subsidiary of Cameron Collective
 *   All Rights Reserved
 */
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Fab from '@mui/material/Fab';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { useState } from 'react';

import { ConfirmDeleteDialog } from 'components';
import { useDeleteDiscountCodeMutation, useGetDiscountCodesQuery } from 'store/api/version-api.slice';

import { DiscountCodeDialog } from './components';
import { styles } from './styles';

const DiscountCodesView = () => {
	const [dialogState, setDialogState] = useState({
		edit: false,
		delete: false,
		add: false
	});
	const [activeDiscountCode, setActiveDiscountCode] = useState(null);

	const { data: codes = {} } = useGetDiscountCodesQuery();
	const [deleteDiscountCode] = useDeleteDiscountCodeMutation();

	const discountCodes = codes.results || [];

	const handleDialogToggle = type => {
		setDialogState(prev => ({
			...prev,
			[type]: !prev[type]
		}));
	};

	const handleDialogClose = () => {
		setDialogState({
			edit: false,
			add: false,
			delete: false
		});
	};

	const handleDeleteDiscountCode = async () => {
		await deleteDiscountCode(activeDiscountCode.id);

		handleDialogClose();
	};

	const openAddDiscountCodeDialog = () => handleDialogToggle('add');
	const openEditDiscountCodeDialog = type => () => {
		setActiveDiscountCode(type);
		handleDialogToggle('edit');
	};
	const openDeleteDiscountCodeDialog = type => () => {
		setActiveDiscountCode(type);
		handleDialogToggle('delete');
	};

	return (
		<Box sx={styles.root}>
			<Box>
				<Paper elevation={2}>
					<Toolbar>
						<Typography component={'span'} variant="h6" color="primary" sx={styles.title}>
							DISCOUNT CODES
						</Typography>
						<Fab size="small" color="primary" onClick={openAddDiscountCodeDialog}>
							<AddIcon />
						</Fab>
					</Toolbar>
					<Divider />
					<List>
						{discountCodes.map((type, index) => (
							<ListItem key={`discount-code-${index}`}>
								<ListItemText primary={type.name} secondary={`${type.code} ${type.percentage}%`} />
								<ListItemSecondaryAction>
									<IconButton onClick={openEditDiscountCodeDialog(type)}>
										<EditIcon />
									</IconButton>
									<IconButton onClick={openDeleteDiscountCodeDialog(type)}>
										<DeleteIcon />
									</IconButton>
								</ListItemSecondaryAction>
							</ListItem>
						))}
					</List>
				</Paper>
				<ConfirmDeleteDialog
					open={dialogState.delete}
					onClose={handleDialogClose}
					title={'Delete Discount Code?'}
					content={'This action is irreversible. Are you sure you wish to delete this discount code?'}
					handleDeleteItem={handleDeleteDiscountCode}
				/>
				{dialogState.edit && (
					<DiscountCodeDialog
						open={dialogState.edit}
						onClose={handleDialogClose}
						discount_code={activeDiscountCode}
					/>
				)}
				{dialogState.add && <DiscountCodeDialog open={dialogState.add} onClose={handleDialogClose} />}
			</Box>
		</Box>
	);
};

export default DiscountCodesView;
